// SNIGJ - Send Notification/Summons/Fine Modal
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { 
  Send, 
  Search, 
  User, 
  AlertTriangle, 
  Bell, 
  DollarSign,
  Calendar,
  MapPin,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const SendNotificationModal = ({ open, onOpenChange, preSelectedCitizen = null }) => {
  const { language } = useAuth();
  const [activeTab, setActiveTab] = useState('notification');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCitizen, setSelectedCitizen] = useState(preSelectedCitizen);
  const [searching, setSearching] = useState(false);

  // Notification form
  const [notificationForm, setNotificationForm] = useState({
    notification_type: 'SUMMONS',
    title: '',
    message: '',
    scheduled_date: '',
    location: '',
    is_urgent: false,
    case_id: ''
  });

  // Fee form
  const [feeForm, setFeeForm] = useState({
    fee_type: 'COURT_FEE',
    amount: '',
    currency: 'USD',
    description: '',
    due_date: '',
    case_id: ''
  });

  useEffect(() => {
    if (preSelectedCitizen) {
      setSelectedCitizen(preSelectedCitizen);
    }
  }, [preSelectedCitizen]);

  const searchCitizens = async () => {
    if (!searchQuery.trim()) return;
    
    setSearching(true);
    try {
      const response = await api.get('/admin/search-citizens', { params: { q: searchQuery } });
      setSearchResults(response.data.citizens || []);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de recherche' : 'Search error');
    } finally {
      setSearching(false);
    }
  };

  const handleSendNotification = async () => {
    if (!selectedCitizen) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner un citoyen' : 'Please select a citizen');
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/notifications', {
        recipient_id: selectedCitizen.user_id,
        ...notificationForm
      });
      toast.success(language === 'fr' ? 'Notification envoyée' : 'Notification sent');
      onOpenChange(false);
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur d\'envoi' : 'Send error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFee = async () => {
    if (!selectedCitizen) {
      toast.error(language === 'fr' ? 'Veuillez sélectionner un citoyen' : 'Please select a citizen');
      return;
    }

    if (!feeForm.amount || parseFloat(feeForm.amount) <= 0) {
      toast.error(language === 'fr' ? 'Montant invalide' : 'Invalid amount');
      return;
    }

    setLoading(true);
    try {
      await api.post('/admin/judicial-fees', {
        citizen_id: selectedCitizen.user_id,
        ...feeForm,
        amount: parseFloat(feeForm.amount)
      });
      toast.success(language === 'fr' ? 'Frais créé et notification envoyée' : 'Fee created and notification sent');
      onOpenChange(false);
      resetForms();
    } catch (error) {
      toast.error(error.response?.data?.detail || (language === 'fr' ? 'Erreur de création' : 'Creation error'));
    } finally {
      setLoading(false);
    }
  };

  const resetForms = () => {
    setNotificationForm({
      notification_type: 'SUMMONS',
      title: '',
      message: '',
      scheduled_date: '',
      location: '',
      is_urgent: false,
      case_id: ''
    });
    setFeeForm({
      fee_type: 'COURT_FEE',
      amount: '',
      currency: 'USD',
      description: '',
      due_date: '',
      case_id: ''
    });
    setSelectedCitizen(null);
    setSearchResults([]);
    setSearchQuery('');
  };

  const notificationTypes = [
    { value: 'SUMMONS', label: language === 'fr' ? 'Convocation' : 'Summons', icon: AlertTriangle },
    { value: 'INVITATION', label: language === 'fr' ? 'Invitation' : 'Invitation', icon: Bell },
    { value: 'REMINDER', label: language === 'fr' ? 'Rappel' : 'Reminder', icon: Calendar },
    { value: 'ALERT', label: language === 'fr' ? 'Alerte' : 'Alert', icon: AlertTriangle },
    { value: 'CASE_UPDATE', label: language === 'fr' ? 'Mise à jour dossier' : 'Case Update', icon: FileText }
  ];

  const feeTypes = [
    { value: 'COURT_FEE', label: language === 'fr' ? 'Frais de justice' : 'Court Fee' },
    { value: 'FINE', label: language === 'fr' ? 'Amende' : 'Fine' },
    { value: 'PENALTY', label: language === 'fr' ? 'Pénalité' : 'Penalty' },
    { value: 'ADMINISTRATIVE_FEE', label: language === 'fr' ? 'Frais administratifs' : 'Administrative Fee' }
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            {language === 'fr' ? 'Envoyer au citoyen' : 'Send to Citizen'}
          </DialogTitle>
          <DialogDescription>
            {language === 'fr' 
              ? 'Envoyez une convocation, invitation ou amende à un citoyen'
              : 'Send a summons, invitation or fine to a citizen'}
          </DialogDescription>
        </DialogHeader>

        {/* Citizen Search */}
        <div className="space-y-4 border-b pb-4">
          <Label className="text-base font-medium">
            {language === 'fr' ? 'Destinataire' : 'Recipient'}
          </Label>
          
          {selectedCitizen ? (
            <div className="flex items-center justify-between p-3 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center">
                  <User className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="font-medium">{selectedCitizen.name}</p>
                  <p className="text-sm text-muted-foreground">{selectedCitizen.email}</p>
                  {selectedCitizen.national_id && (
                    <p className="text-xs text-muted-foreground">ID: {selectedCitizen.national_id}</p>
                  )}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelectedCitizen(null)}>
                {language === 'fr' ? 'Changer' : 'Change'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder={language === 'fr' ? 'Rechercher par nom, email ou ID national...' : 'Search by name, email or national ID...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && searchCitizens()}
                    className="pl-10"
                  />
                </div>
                <Button onClick={searchCitizens} disabled={searching}>
                  {searching ? '...' : (language === 'fr' ? 'Rechercher' : 'Search')}
                </Button>
              </div>

              {searchResults.length > 0 && (
                <div className="border rounded max-h-40 overflow-y-auto">
                  {searchResults.map((citizen) => (
                    <div 
                      key={citizen.user_id}
                      className="flex items-center gap-3 p-2 hover:bg-muted cursor-pointer border-b last:border-b-0"
                      onClick={() => {
                        setSelectedCitizen(citizen);
                        setSearchResults([]);
                      }}
                    >
                      <User className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">{citizen.name}</p>
                        <p className="text-xs text-muted-foreground">{citizen.email}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Tabs for Notification vs Fee */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="notification" className="gap-2">
              <Bell className="w-4 h-4" />
              {language === 'fr' ? 'Convocation / Invitation' : 'Summons / Invitation'}
            </TabsTrigger>
            <TabsTrigger value="fee" className="gap-2">
              <DollarSign className="w-4 h-4" />
              {language === 'fr' ? 'Amende / Frais' : 'Fine / Fee'}
            </TabsTrigger>
          </TabsList>

          {/* Notification Tab */}
          <TabsContent value="notification" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Type de notification' : 'Notification Type'}</Label>
              <Select
                value={notificationForm.notification_type}
                onValueChange={(value) => setNotificationForm({ ...notificationForm, notification_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {notificationTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      <span className="flex items-center gap-2">
                        <type.icon className="w-4 h-4" />
                        {type.label}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
              <Input
                value={notificationForm.title}
                onChange={(e) => setNotificationForm({ ...notificationForm, title: e.target.value })}
                placeholder={language === 'fr' ? 'Ex: Convocation au Tribunal de Paix' : 'Ex: Summons to Peace Court'}
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Message' : 'Message'}</Label>
              <Textarea
                value={notificationForm.message}
                onChange={(e) => setNotificationForm({ ...notificationForm, message: e.target.value })}
                placeholder={language === 'fr' ? 'Détails de la convocation...' : 'Summons details...'}
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {language === 'fr' ? 'Date de convocation' : 'Scheduled Date'}
                </Label>
                <Input
                  type="datetime-local"
                  value={notificationForm.scheduled_date}
                  onChange={(e) => setNotificationForm({ ...notificationForm, scheduled_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  {language === 'fr' ? 'Lieu' : 'Location'}
                </Label>
                <Input
                  value={notificationForm.location}
                  onChange={(e) => setNotificationForm({ ...notificationForm, location: e.target.value })}
                  placeholder={language === 'fr' ? 'Adresse du tribunal' : 'Court address'}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'N° Dossier associé (optionnel)' : 'Related Case ID (optional)'}</Label>
              <Input
                value={notificationForm.case_id}
                onChange={(e) => setNotificationForm({ ...notificationForm, case_id: e.target.value })}
                placeholder="case_xxxx"
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="is_urgent"
                checked={notificationForm.is_urgent}
                onChange={(e) => setNotificationForm({ ...notificationForm, is_urgent: e.target.checked })}
                className="rounded"
              />
              <Label htmlFor="is_urgent" className="text-red-600 dark:text-red-400 flex items-center gap-1">
                <AlertTriangle className="w-4 h-4" />
                {language === 'fr' ? 'Marquer comme URGENT' : 'Mark as URGENT'}
              </Label>
            </div>

            <Button 
              className="w-full gap-2" 
              onClick={handleSendNotification}
              disabled={loading || !selectedCitizen || !notificationForm.title}
            >
              <Send className="w-4 h-4" />
              {loading 
                ? (language === 'fr' ? 'Envoi...' : 'Sending...') 
                : (language === 'fr' ? 'Envoyer la notification' : 'Send Notification')}
            </Button>
          </TabsContent>

          {/* Fee Tab */}
          <TabsContent value="fee" className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Type de frais' : 'Fee Type'}</Label>
              <Select
                value={feeForm.fee_type}
                onValueChange={(value) => setFeeForm({ ...feeForm, fee_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {feeTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Montant' : 'Amount'}</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={feeForm.amount}
                  onChange={(e) => setFeeForm({ ...feeForm, amount: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>{language === 'fr' ? 'Devise' : 'Currency'}</Label>
                <Select
                  value={feeForm.currency}
                  onValueChange={(value) => setFeeForm({ ...feeForm, currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="USD">USD ($)</SelectItem>
                    <SelectItem value="CDF">CDF (FC)</SelectItem>
                    <SelectItem value="EUR">EUR (€)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Description' : 'Description'}</Label>
              <Textarea
                value={feeForm.description}
                onChange={(e) => setFeeForm({ ...feeForm, description: e.target.value })}
                placeholder={language === 'fr' ? 'Motif de l\'amende ou des frais...' : 'Reason for fine or fee...'}
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'Date d\'échéance' : 'Due Date'}</Label>
              <Input
                type="date"
                value={feeForm.due_date}
                onChange={(e) => setFeeForm({ ...feeForm, due_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'fr' ? 'N° Dossier associé (optionnel)' : 'Related Case ID (optional)'}</Label>
              <Input
                value={feeForm.case_id}
                onChange={(e) => setFeeForm({ ...feeForm, case_id: e.target.value })}
                placeholder="case_xxxx"
              />
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800 text-sm">
              <p className="text-blue-700 dark:text-blue-300">
                {language === 'fr' 
                  ? 'Le citoyen recevra automatiquement une notification avec un lien de paiement.'
                  : 'The citizen will automatically receive a notification with a payment link.'}
              </p>
            </div>

            <Button 
              className="w-full gap-2" 
              onClick={handleCreateFee}
              disabled={loading || !selectedCitizen || !feeForm.amount || !feeForm.description}
            >
              <DollarSign className="w-4 h-4" />
              {loading 
                ? (language === 'fr' ? 'Création...' : 'Creating...') 
                : (language === 'fr' ? 'Créer l\'amende/frais' : 'Create Fine/Fee')}
            </Button>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default SendNotificationModal;
